import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { Button } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel } from "@fortawesome/free-solid-svg-icons";

// Aire alrededor del bloque resaltado, para que el anillo no quede pegado al
// texto.
const PADDING = 8;
const CALLOUT_GAP = 14;
const CALLOUT_WIDTH = 340;
// Lo que tarda en irse el modal de ayuda (fade de reactstrap): si el
// resaltado aparece antes, se pisan los dos fondos oscuros.
const SHOW_DELAY = 350;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * Resaltado tipo tutorial de la carga inicial en el Inicio.
 *
 * En vez de subir el z-index del bloque (no sirve si algun ancestro crea su
 * propio contexto de apilamiento), se tapa todo lo demas con cuatro paneles
 * alrededor del hueco. Asi el bloque resaltado sigue siendo clickeable tal
 * cual esta, y un clic en cualquier panel cierra el resaltado.
 */
const InitialLoadSpotlight = ({ isOpen, targetRef, onClose, onStart }) => {
  const [ready, setReady] = useState(false);
  const [rect, setRect] = useState(null);
  const primaryRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setReady(false);
      return undefined;
    }
    const timer = setTimeout(() => setReady(true), SHOW_DELAY);
    return () => clearTimeout(timer);
  }, [isOpen]);

  const active = isOpen && ready;

  // Lleva el bloque al centro de la pantalla y sigue su posicion: puede
  // moverse por scroll, por cambio de tamaño de la ventana o porque el paso
  // de bienvenida termina de desplegarse.
  useLayoutEffect(() => {
    const target = targetRef.current;
    if (!active || !target) {
      return undefined;
    }

    target.scrollIntoView({
      block: "center",
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });

    let frame = null;
    const measure = () => {
      frame = null;
      const box = target.getBoundingClientRect();
      setRect({
        top: box.top - PADDING,
        left: box.left - PADDING,
        width: box.width + PADDING * 2,
        height: box.height + PADDING * 2,
      });
    };
    const schedule = () => {
      if (frame === null) frame = requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    observer?.observe(target);

    return () => {
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
      observer?.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [active, targetRef]);

  useEffect(() => {
    if (!active) {
      return undefined;
    }
    primaryRef.current?.focus({ preventScroll: true });
    const onKeyDown = (event) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [active, onClose]);

  if (!active || !rect) {
    return null;
  }

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const right = rect.left + rect.width;
  const bottom = rect.top + rect.height;

  // La tarjeta va debajo del bloque si entra; si no, arriba; y si tampoco
  // (pantallas chicas), pegada al borde inferior.
  const calloutWidth = Math.min(CALLOUT_WIDTH, viewportWidth - 32);
  const calloutLeft = Math.min(
    Math.max(16, rect.left),
    viewportWidth - calloutWidth - 16
  );
  const estimatedHeight = 190;
  let calloutStyle;
  if (bottom + CALLOUT_GAP + estimatedHeight <= viewportHeight) {
    calloutStyle = { top: bottom + CALLOUT_GAP };
  } else if (rect.top - CALLOUT_GAP - estimatedHeight >= 0) {
    calloutStyle = { bottom: viewportHeight - rect.top + CALLOUT_GAP };
  } else {
    calloutStyle = { bottom: 16 };
  }

  const shades = [
    { top: 0, left: 0, width: "100%", height: Math.max(0, rect.top) },
    { top: bottom, left: 0, width: "100%", bottom: 0 },
    { top: rect.top, left: 0, width: Math.max(0, rect.left), height: rect.height },
    { top: rect.top, left: right, right: 0, height: rect.height },
  ];

  return createPortal(
    <div className="initial-load-spotlight">
      {shades.map((style, index) => (
        <div
          key={index}
          className="initial-load-spotlight__shade"
          style={style}
          onClick={onClose}
          aria-hidden="true"
        />
      ))}
      <div
        className="initial-load-spotlight__ring"
        style={rect}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-labelledby="initial-load-spotlight-title"
        aria-describedby="initial-load-spotlight-description"
        className="initial-load-spotlight__callout card"
        style={{ ...calloutStyle, left: calloutLeft, width: calloutWidth }}
      >
        <div className="card-body p-3">
          <span className="initial-load-spotlight__eyebrow text-primary">
            EMPIEZA POR AQUÍ
          </span>
          <h5
            id="initial-load-spotlight-title"
            className="fs-0 font-weight-semi-bold text-900 mt-1 mb-2"
          >
            Carga tu condominio en un solo archivo
          </h5>
          <p
            id="initial-load-spotlight-description"
            className="fs--1 text-700 mb-3"
          >
            Descarga el archivo, completa propietarios, alícuotas y saldos, y
            súbelo. También puedes agendar una llamada y lo hacemos contigo.
          </p>
          <div className="d-flex flex-wrap align-items-center">
            <Button
              innerRef={primaryRef}
              color="primary"
              size="sm"
              className="mr-2"
              onClick={onStart}
            >
              <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
              Hacer la carga inicial
            </Button>
            <Button color="link" size="sm" className="text-700" onClick={onClose}>
              Entendido
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

InitialLoadSpotlight.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  targetRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  onClose: PropTypes.func.isRequired,
  onStart: PropTypes.func.isRequired,
};

export default InitialLoadSpotlight;
